import { useEffect, useState } from 'react'
import { Alert, Button, Group, NumberInput, Stack, Text } from '@mantine/core'
import { useSalvarTeto, useSimulacaoDeTeto } from '@/hooks/useSoc'

/**
 * Quantas ligações o SOC faz por hora para a mesma pessoa.
 *
 * O CAMPO NÃO É O PONTO — a linha de consequência é. Escolher "3" no escuro é
 * diferente de ver o que "3" significa para ESTE time, com os eventos reais
 * dele. Número que não muda decisão é enfeite; este muda.
 *
 * Medido no tenant 94 em 13/09/2026: 81 eventos críticos em 30 dias, 54 deles
 * entre 00h e 06h. Sem teto, isso são 54 ligações de madrugada.
 */
export function TetoDeLigacoes({
  teamId,
  valorAtual,
}: {
  teamId: number
  valorAtual: number | null
}) {
  const [valor, setValor] = useState<number | null>(valorAtual)
  const salvar = useSalvarTeto(teamId)
  const simulacao = useSimulacaoDeTeto(teamId, valor)

  // O time pode mudar embaixo do componente (o pai troca `teamId` sem
  // desmontar); sem isto o campo mostraria o teto do time anterior.
  useEffect(() => {
    setValor(valorAtual)
  }, [teamId, valorAtual])

  const mudou = valor !== valorAtual
  const invalido = valor != null && valor < 1

  return (
    <Stack gap="xs">
      <Group align="flex-end" gap="sm">
        <NumberInput
          label="Teto de ligações"
          description="por hora, para cada plantonista"
          placeholder="sem teto"
          min={1}
          max={99}
          w={200}
          value={valor ?? ''}
          onChange={(v) => setValor(v === '' || v == null ? null : Number(v))}
          error={
            invalido
              ? 'Use 1 ou mais. Para desligar a voz, use SOC_VOICE_ENABLED.'
              : undefined
          }
        />
        <Button
          disabled={!mudou || invalido}
          loading={salvar.isPending}
          onClick={() => salvar.mutate(valor)}
        >
          Salvar
        </Button>
        {valorAtual != null && valor != null && (
          <Button variant="subtle" onClick={() => setValor(null)}>
            Remover teto
          </Button>
        )}
      </Group>

      {valor == null && (
        <Text size="sm" c="dimmed">
          Sem teto: cada alerta crítico gera uma ligação.
        </Text>
      )}

      {valor != null && !invalido && simulacao.data && (
        <Alert
          variant="light"
          color={simulacao.data.com_teto < simulacao.data.sem_teto ? 'blue' : 'gray'}
        >
          <Text size="sm">
            Nos últimos {simulacao.data.dias} dias, este teto teria gerado{' '}
            <b>{simulacao.data.com_teto} ligações</b> em vez de{' '}
            <b>{simulacao.data.sem_teto}</b>.
          </Text>
          {/*
            A ressalva anda JUNTO do número, e não num tooltip: um número sem o
            que ele não mede é o que contamina decisão em silêncio.
          */}
          <Text size="xs" c="dimmed" mt={4}>
            Estimativa sobre eventos passados. Não considera silenciamentos, e a
            contagem real depende de quem estiver na escala — o teto é por pessoa.
          </Text>
        </Alert>
      )}

      {valor != null && !invalido && simulacao.data?.politicas_ativas === 0 && (
        <Text size="sm" c="dimmed">
          Este time ainda não assina nenhum alerta, então não há o que simular.
        </Text>
      )}
    </Stack>
  )
}
